import { useCallback, useEffect, useRef, useState } from 'react';
import { IDLE_RECORDING, isRecordingInFlight, type RecordingStatus } from '@liveforge/shared';
import { errorMessage } from '../lib/api';
import { startDownload } from '../lib/download';
import {
  abandonRecording,
  getRecordingStatus,
  recordingDownloadLink,
  startRecording,
  stopRecording,
} from '../lib/resources';

/**
 * Recording a Live, driven from the studio.
 *
 * The state is read from the server rather than assumed from the last button
 * press, because Egress can stop on its own - a crash, a disk filling, a limit
 * reached - and a director who is told they are recording when they are not
 * loses the show, not a feature.
 */

/**
 * How often the studio asks. Slow: this is a status line, not a transport, and
 * each poll is a round trip to LiveKit through our own API.
 */
const IDLE_POLL_MS = 15_000;
/** Faster while something is in flight, where seconds are what change. */
const ACTIVE_POLL_MS = 4_000;

export function useRecording(liveId: string | undefined) {
  const [status, setStatus] = useState<RecordingStatus>(IDLE_RECORDING);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Read by the poll loop, which must not restart every time the status
  // changes. Written in an effect, never during render: React forbids mutating
  // a ref while rendering, and the poll that reads it runs from a timer.
  //
  // `in flight` and not `active`: a job that is stopping is no longer
  // recording, but Egress moves it to complete within seconds and a fifteen
  // second poll would leave the studio saying "Stopping" long after the file
  // was finished. Every state that resolves on its own is polled quickly.
  const inFlight = useRef(false);
  useEffect(() => {
    inFlight.current = isRecordingInFlight(status);
  }, [status]);

  useEffect(() => {
    if (!liveId) return;

    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const next = await getRecordingStatus(liveId);
        if (cancelled) return;
        setStatus(next);
        setError(null);
      } catch (cause) {
        if (cancelled) return;
        // Not cleared on the next success by accident: a status the studio
        // cannot read is worth saying, because the button below it is about to
        // lie in one direction or the other.
        setError(errorMessage(cause));
      }
      if (!cancelled) {
        timer = window.setTimeout(
          () => void poll(),
          inFlight.current ? ACTIVE_POLL_MS : IDLE_POLL_MS,
        );
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [liveId]);

  const run = useCallback(
    async (action: (id: string) => Promise<RecordingStatus>) => {
      if (!liveId || busy) return;
      setBusy(true);
      setError(null);
      try {
        setStatus(await action(liveId));
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setBusy(false);
      }
    },
    [liveId, busy],
  );

  /**
   * Asks the API for a signed link, then lets the browser fetch the file.
   *
   * Deliberately not a fetch into a blob: a recording is gigabytes, and
   * buffering one in the tab to attach an Authorization header would run the
   * page out of memory. The link carries its own short-lived credential, so
   * the browser streams it to disk the way it streams any download.
   */
  const download = useCallback(async () => {
    if (!liveId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await recordingDownloadLink(liveId);
      // Never a navigation: unloading the SPA would disconnect the room and
      // end the show. See `startDownload`.
      startDownload(url);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }, [liveId, busy]);

  return {
    status,
    /**
     * Set when the *status* could not be read - which is not the same as being
     * idle. A studio that cannot see Egress must say so, because the control
     * beside it is about to lie in one direction or the other.
     */
    error,
    busy,
    /**
     * `debug` puts the compositor's diagnostic overlay into the file itself.
     * Alt-click in the studio, and see the Decision Log for what it answers.
     */
    start: useCallback((debug = false) => void run((id) => startRecording(id, debug)), [run]),
    stop: useCallback(() => void run(stopRecording), [run]),
    abandon: useCallback(() => void run(abandonRecording), [run]),
    download: useCallback(() => void download(), [download]),
    dismissError: useCallback(() => setError(null), []),
  };
}
